import { formatApiMessage } from "./apiMessageFormat";
import { getDisplayLanguage } from "./language";
import { translate, useLanguage } from "./language";
/**
 * Minimal toast queue. Deliberately tiny — the platform needs "the save worked"
 * and "the server said no", not a notification framework.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError } from "./api";

type ToastKind = "info" | "success" | "error";

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  push: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  /** Turns an unknown thrown value into a readable toast. */
  error: (err: unknown, fallback?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const LIFETIME_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  useLanguage();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, kind: ToastKind = "info") => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, kind, message }]);
      window.setTimeout(() => remove(id), LIFETIME_MS);
    },
    [remove],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (message) => push(message, "success"),
      error: (err, fallback = "Something went wrong") => {
        // A step-up response immediately redirects to the shared passkey flow.
        // Suppress the stale failure toast so the user sees that prompt instead.
        if (err instanceof ApiError && err.isStepUpRequired) return;
        let message = fallback;
        if (err instanceof ApiError) {
          message = err.isForbidden
            ? `Not permitted: ${err.originalMessage}`
            : err.status === 0
              ? "Could not reach the hub API. Check your connection."
              : err.originalMessage;
        } else if (err instanceof Error && err.message) {
          message = err.message;
        }
        push(message, "error");
      },
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* aria-live so a screen reader hears the result of an action it did not
          visually observe. */}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast${t.kind === "error" ? " toast--error" : t.kind === "success" ? " toast--success" : ""}`}
          >
            <span style={{ flex: 1 }}>{formatApiMessage(translate(t.message),getDisplayLanguage())}</span>
            <button
              className="btn btn--ghost btn--sm btn--icon"
              aria-label={translate("Dismiss")}
              onClick={() => remove(t.id)}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
